import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Eye,
  MessageSquare,
  Clock,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { playerAuthService } from '@/lib/services/player-auth.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import PageHero from '@/components/common/PageHero';
import { toast } from 'sonner';
import { ticketService, type Ticket, type TicketCategory, type TicketStatus, type TicketPriority } from '@/lib/services/ticket.service';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Caixa } from '@/construtor/caixa';

const statusColors: Record<TicketStatus, string> = {
  OPEN: 'bg-primary',
  IN_PROGRESS: 'bg-warning',
  AWAITING_CUSTOMER: 'bg-warning',
  RESOLVED: 'bg-success',
  CLOSED: 'bg-muted',
};

const statusLabels: Record<TicketStatus, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em Andamento',
  AWAITING_CUSTOMER: 'Aguardando Resposta',
  RESOLVED: 'Resolvido',
  CLOSED: 'Fechado',
};

const priorityLabels: Record<TicketPriority, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

function hasPermission(permission: string): boolean {
  const player = playerAuthService.getPlayer();
  const perms: string[] = (player?.role as any)?.permissions ?? [];
  return perms.includes(permission);
}

export default function SupportPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, openLoginModal } = useAuth();
  const { config } = useTheme();
  const { store } = useStore();
  const isStaffPlayer = isAuthenticated && hasPermission('SUPPORT_VIEW_TICKETS');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    categoryId: '',
    priority: 'MEDIUM' as TicketPriority,
    message: '',
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (store?.id && isAuthenticated) {
      loadCategories();
      loadTickets();
    }
  }, [store?.id, isAuthenticated]);

  const loadCategories = async () => {
    if (!store?.id) return;
    try {
      const data = await ticketService.listActiveCategories(store.id);
      setCategories(data.sort((a, b) => a.order - b.order));
    } catch (error: any) {
      console.error('Error loading categories:', error);
      toast.error('Erro ao carregar categorias');
    }
  };

  const loadTickets = async () => {
    if (!store?.id) return;
    try {
      setLoading(true);
      const data = await ticketService.listPlayerTickets(store.id);
      setTickets(data);
    } catch (error: any) {
      console.error('Error loading tickets:', error);
      toast.error('Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!store?.id) return;
    if (!newTicket.title || !newTicket.categoryId || !newTicket.message) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      setCreating(true);
      await ticketService.createTicket(store.id, {
        title: newTicket.title,
        categoryId: newTicket.categoryId,
        priority: newTicket.priority,
        message: newTicket.message,
      });
      setNewTicket({ title: '', categoryId: '', priority: 'MEDIUM', message: '' });
      setIsDialogOpen(false);
      toast.success('Ticket criado com sucesso!');
      loadTickets();
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      toast.error('Erro ao criar ticket');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status: TicketStatus) => {
    return (
      <Badge className={statusColors[status]}>
        {statusLabels[status]}
      </Badge>
    );
  };

  const handleViewTicket = (ticketId: string) => {
    navigate(`/suporte/ticket/${ticketId}`);
  };

  const filteredTickets = tickets.filter(
    (ticket) =>
      ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <div>
        <PageHero
          title="Tickets"
          breadcrumbs={[
            { label: 'Suporte', href: '/suporte' },
            { label: 'Tickets' },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                Você precisa estar logado para acessar os tickets
              </p>
              <Button className="mt-4" onClick={openLoginModal}>
                Fazer Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title="Tickets"
        breadcrumbs={[
          { label: 'Suporte', href: '/suporte' },
          { label: 'Tickets' },
        ]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Header with Search and Create Button */}
        <Caixa id="suporte.topo">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          {/* Search */}
          <Caixa id="suporte.busca">
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar tickets..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          </Caixa>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {isStaffPlayer && (
              <Button
                variant="outline"
                className="whitespace-nowrap"
                onClick={() => navigate('/suporte/staff')}
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                Painel de Atendimento
              </Button>
            )}

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-hover text-primary-foreground whitespace-nowrap">
                <Plus className="w-4 h-4 mr-2" />
                Abrir Ticket
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Abrir Novo Ticket</DialogTitle>
                <DialogDescription>
                  Descreva seu problema e nossa equipe irá ajudá-lo o mais rápido
                  possível.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="ticket-title">Título</Label>
                  <Input
                    id="ticket-title"
                    placeholder="Resumo do problema"
                    value={newTicket.title}
                    onChange={(e) =>
                      setNewTicket({ ...newTicket, title: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ticket-category">Categoria</Label>
                  <Select
                    value={newTicket.categoryId}
                    onValueChange={(value) =>
                      setNewTicket({ ...newTicket, categoryId: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ticket-priority">Prioridade</Label>
                  <Select
                    value={newTicket.priority}
                    onValueChange={(value) =>
                      setNewTicket({ ...newTicket, priority: value as TicketPriority })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Baixa</SelectItem>
                      <SelectItem value="MEDIUM">Média</SelectItem>
                      <SelectItem value="HIGH">Alta</SelectItem>
                      <SelectItem value="URGENT">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ticket-message">Mensagem</Label>
                  <Textarea
                    id="ticket-message"
                    placeholder="Descreva detalhadamente o seu problema..."
                    rows={5}
                    value={newTicket.message}
                    onChange={(e) =>
                      setNewTicket({ ...newTicket, message: e.target.value })
                    }
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={creating}
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-primary hover:bg-primary-hover text-primary-foreground"
                  onClick={handleCreateTicket}
                  disabled={creating}
                >
                  {creating ? 'Criando...' : 'Criar Ticket'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>
        </Caixa>

        {/* Tickets Table */}
        <Caixa id="suporte.tickets" miolo>
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <p className="text-muted-foreground">Carregando tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              /*
                Mesmo desenho do vazio de formulários: quadrado arredondado na
                cor da loja, título em destaque e uma linha dizendo o que fazer.
                O ícone estava cinza sobre círculo cinza — lia como algo
                desativado, e não como uma lista que ainda não tem nada.
              */
              <Caixa id="suporte.vazio">
              <div className="p-12 flex flex-col items-center text-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="w-10 h-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {t(
                      searchQuery
                        ? 'store.support.emptySearchTitle'
                        : 'store.support.emptyTitle',
                    )}
                  </h3>
                  <p className="text-muted-foreground">
                    {t(
                      searchQuery
                        ? 'store.support.emptySearchBody'
                        : 'store.support.emptyBody',
                    )}
                  </p>
                </div>
              </div>
              </Caixa>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="text-muted-foreground">#ID</TableHead>
                    <TableHead className="text-muted-foreground">Título</TableHead>
                    <TableHead className="text-muted-foreground">Prioridade</TableHead>
                    <TableHead className="text-muted-foreground">
                      Criado há
                    </TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground text-right">
                      Ações
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <Caixa id="suporte.tickets.linha" repetida key={ticket.id}>
                    <TableRow
                      key={ticket.id}
                      className="table-row-interactive border-border"
                    >
                      <TableCell className="font-medium text-muted-foreground font-mono text-xs">
                        #{ticket.id.slice(0, 8)}
                      </TableCell>
                      <TableCell className="font-medium text-foreground">
                        {ticket.title}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{priorityLabels[ticket.priority]}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(new Date(ticket.createdAt), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleViewTicket(ticket.id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    </Caixa>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        </Caixa>
      </div>
    </div>
  );
}
